
import { CircleIcon, Button, PrimaryButton, ReactIcons } from "@kalyzee/kalyzee-ui";
import { UserAvatar } from "@kalyzee/kast-web-sdk/web-components";
import { MeetingSession } from "@kalyzee/kast-web-sdk/websocket";
import React, { useCallback, useMemo } from "react";
import { PopulatedSession } from "../../../contexts/meetingUtils/interfaces";
import { getNormalUser } from "../../../helpers/user";
import { List } from "../../common/List";
import { SectionWorkBox } from "../SectionWorkBox";
import { SdkContextState } from "../../../contexts/sdk/context";
import { toastError } from "../../../helpers/toast";
import { StringInterval } from "../../utils/StringInterval";
import { HighlightedSessions } from "../../../interfaces/highlight";
import { UserCardAvatarArea } from "../../common/UserCardAvatarArea";

import styles from "./HighlightedUserListWorkBox.module.css";

const Item = React.memo(({ 
  session,
  sessionIncr,
  extra,
  subtitle,
}: { 
  session: MeetingSession,
  sessionIncr: number;
  extra: React.ReactNode,
  subtitle?: React.ReactNode,
}) => {
  const user = session.user;
  if (!user) return null;
  const { avatar, displayName } = getNormalUser(user);
  return (
    <div style={{
      display: "flex",
      alignItems: "center",
      gap: "0.5em",
      justifyContent: "space-between",
    }}>
      <div style={{
        display: "flex",
        alignItems: "center",
        gap: "0.5em",
      }}>
        <UserCardAvatarArea user={{ id: user.id, username: displayName, avatar }}>
          <UserAvatar
            mode="image"
            image={avatar}
            username={displayName}
            style={{ height: "2em", width: "2em" }}
          />
        </UserCardAvatarArea>
        <div style={{ display: "flex", flexDirection: "column", fontSize: "1em", lineHeight: "1em" }}>
          {displayName}{sessionIncr > 0 ? ` (${sessionIncr})` : ""}
          {subtitle && (
            <span style={{ fontSize: "0.7em", color: "var(--color-gray-1)" }}>
              {subtitle}
            </span>
          )}
        </div>
      </div>
      {extra}
    </div>
  );
});

export interface HighlightedUserListProps {
  sdk: SdkContextState,
  highlightSessions: HighlightedSessions;
  style?: React.CSSProperties,
  foldableSections?: boolean;
  enabledSections?: {
    requesting?: boolean;
    ongoing?: boolean;
    inactive?: boolean;
  },
  onClose?: () => void;
}

function renderList<T>(items: T[], render: (item: T) => React.ReactNode, emptyPlaceholder: string)  {
  return (
    <List
      style={{ padding: items.length ? "0.5em 1em" : 0, gap: 5 }}
      items={items}
      renderItem={render}
      emptyPlaceholder={<div style={{opacity: 0.7}}>{emptyPlaceholder}</div>}
    />
  );
};

// un hook dans meeting.tsx qui créé la data de la box
// un deuxième hook qui observe cet data et lance une popup si besoin

export const HighlightedUserListWorkBox = React.memo(({
  sdk,
  highlightSessions,
  style,
  foldableSections,
  enabledSections = {
    requesting: true,
    ongoing: true,
    inactive: true,
  },
  onClose,
}: HighlightedUserListProps) => {
  const onHighlight = useCallback(async (session: PopulatedSession) => {
    const client = sdk.client.getWebsocketClient();
    if (!client) return;

    const { error, response } = await client.emitter.meeting.startHighlighting({
      sessionId: session.id,
    });

    if (error || !response) {
      if (!error?.message.includes("is already ongoing")) {
        toastError("Une erreur est apparue.");
        return;
      }
    }
  }, [sdk]);

  const onRemoveHighlight = useCallback(async (session: PopulatedSession) => {
    const client = sdk.client.getWebsocketClient();
    if (!client) return;

    const { error, response } = await client.emitter.meeting.stopHighlighting({
      sessionId: session.id,
    });
    
    if (error || !response) {
      toastError("Une erreur est apparue.");
    }
  }, [sdk]);

  const onAnswerRequest = useCallback(async (session: PopulatedSession, accepted: boolean) => {
    const client = sdk.client.getWebsocketClient();
    if (!client) return;

    const { error, response } = await client.emitter.meeting.respondToTheRequestForHighlighting({
      sessionId: session.id,
      accepted,
    });
    
    if (error || !response) {
      toastError("Une erreur est apparue.");
    }
  }, [sdk]);

  const renderRequestsSection = () => {
    return renderList(highlightSessions.requesting, session => (
      <Item
        session={session.session}
        sessionIncr={session.increment}
        extra={
          <div style={{ display: "flex", gap: "0.5em", fontSize: "0.6em" }}>
            <PrimaryButton onClick={() => onAnswerRequest(session, true)}>
              <CircleIcon.Variants.ChoiceOK />
            </PrimaryButton>
            <PrimaryButton onClick={() => onAnswerRequest(session, false)}>
              <CircleIcon.Variants.ChoiceKO />
            </PrimaryButton>
          </div>
        }
      />
    ), 'Aucune demande');
  };

  const renderOngoingSection = () => {
    return renderList(highlightSessions.ongoing, session => (
      <Item
        session={session.session.session}
        sessionIncr={session.session.increment}
        extra={
          <Button.Simple 
            style={{ padding: "0.2em 0.5em" }}
            onClick={() => onRemoveHighlight(session.session)}
            label="Retirer"
            className={styles.removeBtn}
          />
        }
        subtitle={session.pending ? (
          <span>En attente de confirmation <StringInterval delay={500} strings={["", ".", "..", "..."]} /></span>
        ) : null}
      />
    ), 'Aucun intervenant');
  };

  const renderOthersSection = () => {
    return renderList(highlightSessions.inactive, session => (
      <Item
        session={session.session}
        sessionIncr={session.increment}
        extra={
          <Button.Simple
            style={{ padding: "0.2em 0.5em" }}
            label="Ajouter"
            onClick={() => onHighlight(session)}
          />
        }
      />
    ), 'Aucun utilisateur disponible');
  };

  const sections = useMemo(() => {
    const _sections = [];
    if (enabledSections.requesting) {
      _sections.push({
        key: 'highlight_userlist_requesting',
        priority: 2,
        left: <ReactIcons.Hand2 style={{ width: "1.5em", height: "1.5em" }} />,
        foldable: true,
        header: `Demandes d'intervention (${highlightSessions.requesting.length})`,
        content: renderRequestsSection(),
      });
    }
    if (enabledSections.ongoing) {
      _sections.push({
        key: 'highlight_userlist_ongoing',
        priority: 2,
        foldable: true,
        header: `Interventions en cours (${highlightSessions.ongoing.length})`,
        content: renderOngoingSection(),
      });
    }
    if (enabledSections.inactive) {
      _sections.push({
        key: 'highlight_userlist_inactive',
        foldable: true,
        priority: 1,
        header: `Inviter des intervenants (${highlightSessions.inactive.length})`,
        content: renderOthersSection(),
      });
    }
    return _sections;
  }, [enabledSections, highlightSessions]);
    
  return (
    <SectionWorkBox
      style={style}
      foldable={foldableSections}
      sections={sections}
      onClose={onClose}
    />
  );
});